import React, {useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Save, Search, FileText, BarChart, 
  History, Settings, Share2, Filter, Bookmark, 
  Trash2, ShieldAlert, ChevronDown, Activity, 
  Clock, Move, CheckCircle, Play, CheckCircle2, 
  XCircle, DollarSign, ShoppingCart, AlertCircle,
  List, Printer, FileSpreadsheet, Box, FileCheck 
} from 'lucide-react';

  const MODULE_PERM_MAP = {
    'ORGANIZATION': 'organizations',
    'ASSET': 'assets',
    'LOCATION': 'locations',      
    'WORKORDER': 'workorders',
    'INVENTORY': 'inventory',
    'CLASSIFICATION': 'classifications',
    'VENDOR': 'vendors',
    'METER': 'meters',   
    'DOMAINS': 'domains',
    'CRAFTS': 'craft',
    'STOREROOMS': 'storerooms',
    'ITEMS': 'items',
    'INVENTORYUSAGE': 'invusage',
    'MR': 'mr',
    'PR': 'pr',
    'PO': 'po',
    'LABOR': 'labor',
    'PM': 'pm',
    'SAFETY': 'safety',
    'HAZARD': 'hazards',
    'TAGOUT': 'tagouts',
    'PRECUTION': 'precautions',
    'SR': 'sr',
    'INCIDENT': 'incidents',
    'ASSET_CATEGORIES': 'asset-categories',
    'FAILURECLASS': 'workorders',
    'RFQ': 'rfq',
  };

const AppSidebar = ({ appName, viewType }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [pendingPO, setPendingPO] = useState(null);
  const [pendingPRs, setPendingPRs] = useState([]);
  const [currentPRForPO, setCurrentPRForPO] = useState(null);
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const permKey = MODULE_PERM_MAP[appName] || appName?.toLowerCase();
  const { canCreate, canUpdate, canDelete } = usePermissions(permKey);
  const isNewRecord = location.pathname.endsWith('/new');
  const translatedApp = t(`apps.${appName}`) || appName;
// دالة ذكية لمراقبة الـ URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    // لو إحنا في صفحة تفاصيل PR (مثلاً /pr/PR1001)
    if (pathParts[1] === 'pr' && pathParts[2] && pathParts[2] !== 'new') {
      const prNum = pathParts[2];
      checkIfPRIsApproved(prNum);
    } else {
      setCurrentPRForPO(null); // اخفي المنيو لو طلعنا من الصفحة
    }
  }, [location.pathname]);

  const checkIfPRIsApproved = async (prNum) => {
    try {
      const res = await axios.get(`/api/pr/check-pending-po/${session.org_id}/${prNum}`);
      if (res.data.show_menu) {
        setCurrentPRForPO(res.data.pr);
      } else {
        setCurrentPRForPO(null);
      }
    } catch (err) {
      setCurrentPRForPO(null);
    }
  };

  // ... استماع للـ CustomEvent برضه عشان التحديث اللحظي ...
  useEffect(() => {
    const handleInstantUpdate = () => {
      const prNum = location.pathname.split('/')[2];
      if (prNum) checkIfPRIsApproved(prNum);
    };
    window.addEventListener('SHOW_CREATE_PO_MENU', handleInstantUpdate);
    return () => window.removeEventListener('SHOW_CREATE_PO_MENU', handleInstantUpdate);
  }, [location.pathname]);
  
  // تفعيل زر الحفظ عبر إرسال حدث (Custom Event)
  const triggerSave = () => {
    const event = new CustomEvent('GLOBAL_SAVE_HANDLER');
    window.dispatchEvent(event);
  };

  // دالة التعامل مع إنشاء سجل جديد
  const handleNewRecord = () => {
    const routes = {
      'ORGANIZATION': '/organizations/new',
      'ASSET': '/assets/new',
      'LOCATION': '/locations/new',      
      'WORKORDER': '/workorders/new',
      'INVENTORY': '/inventory/new',
      'CLASSIFICATION': '/classifications/new',
      'VENDOR': '/vendors/new',
      'METER': '/meters/new',   
      'DOMAINS': '/domains/new',
      'CRAFTS': '/crafts/new',
      'STOREROOMS': '/storerooms/new',
      'ITEMS': '/items/new',
      'INVENTORYUSAGE': '/inventoryusage/new',
      'MR': '/mr/new',
      'PR': '/pr/new',
      'PO': '/po/new',
      'LABOR': '/labors/new',
      'PM': '/pm/new',
      'SAFETY': '/safety/new',
      'HAZARD': '/hazards/new',
      'TAGOUT': '/tagouts/new',
      'PRECUTION': '/precautions/new',
      'TOOLS': '/tools/new',
      'SR': '/sr/new',
      'INCIDENTS': '/incidents/new',
      'ASSET_CATEGORIES': '/asset-categories/new',
      'FAILURE_CODES': '/failure-codes/new',
      'CONTRACT': '/contracts/new',
      'REPORTLIST': '/reportdesigner/new',
      'RFQ': '/rfq/new',
      'VENDORS': '/vendors/new',
      'ROLE': '/roles/new',
    };
    const targetPath = routes[appName] || '/dashboard';
    navigate(targetPath);
    
  };


  // دالة مساعدة لرسم الأزرار بتصميم موحد
  const SidebarButton = ({ icon, label, onClick, className = "", visible = true }) => {
    if (!visible) return null;
    return (
      <button 
        onClick={onClick}
        className={`w-full text-left px-3 py-2 text-[12px] text-gray-600 hover:bg-[#005495] hover:text-white flex items-center gap-3 group transition-all rounded-sm ${className}`}
      >
        <span className="text-gray-400 group-hover:text-white transition-colors">{icon}</span>
        <span className="font-medium">{label}</span>
      </button>
    );
  };

  // ==========================================
  // 1.        (ListView Content)
  // ==========================================
  const renderListViewContent = () => {
    if (appName === 'ASSET') {
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.queries')}</div>
          <nav className="p-1">
            <SidebarButton icon={<Filter size={14}/>} label={t('sidebar.all_assets')} onClick={() => {
               const event = new CustomEvent('GLOBAL_VIEW_ALL_SITES');
               window.dispatchEvent(event);
              }}/>
            <SidebarButton icon={<Activity size={14}/>} label={t('sidebar.operating_assets')} onClick={() => {
               const event = new CustomEvent('GLOBAL_VIEW_OPERATING');
               window.dispatchEvent(event);
              }}/>
            <SidebarButton icon={<ShieldAlert size={14}/>} label={t('sidebar.critical_assets')} />
            <SidebarButton icon={<Bookmark size={14}/>} label={t('sidebar.my_bookmarks')} />
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.common_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={canUpdate} icon={<Move size={14}/>} label={t('sidebar.move_assets')}  onClick={() => {
               const event = new CustomEvent('GLOBAL_BULK_MOVE_HANDLER');
               window.dispatchEvent(event);
              }}/>
            <SidebarButton icon={<Printer size={14}/>} label={t('sidebar.print_barcodes')} />
            <SidebarButton icon={<BarChart size={14}/>} label={t('sidebar.create_report')} />
          </nav>
        </>
      );
    }

    if (appName === 'WORKORDER') {
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.wo_queries')}</div>
          <nav className="p-1">
            <SidebarButton icon={<Filter size={14}/>} label={t('sidebar.all_wo')}  onClick={() => {
               const event = new CustomEvent('GLOBAL_VIEW_ALL_SITES');
               window.dispatchEvent(event);
              }}
            />
            <SidebarButton icon={<Clock size={14}/>} label={t('sidebar.waiting_approval')} onClick={() => {
               const event = new CustomEvent('GLOBAL_WAPPR');
               console.log("Clicked");
               window.dispatchEvent(event);
              }}/>
            <SidebarButton icon={<Play size={14}/>} label={t('sidebar.wip')} onClick={() => {
               const event = new CustomEvent('GLOBAL_VIEW_INPRG');
               console.log("Clicked");
               window.dispatchEvent(event);
              }}/>
            <SidebarButton icon={<CheckCircle size={14}/>} label={t('sidebar.completed_today')} onClick={() => {
               const event = new CustomEvent('GLOBAL_VIEW_COMP_TODAY');
               console.log("Clicked");
               window.dispatchEvent(event);
              }}/>
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.common_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={canUpdate} icon={<CheckCircle size={14}/>} label={t('sidebar.bulk_approve')} />
          </nav>
        </>
      );
    }
    //if (appName === 'INVENTORY' ){return null;}
    // Default List 
    return (
      <>
        <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.queries')}</div>
        <nav className="p-1">
          <SidebarButton icon={<Filter size={14}/>} label={t('sidebar.all_records')} onClick={() => {
               const event = new CustomEvent('GLOBAL_VIEW_ALL_SITES');
               console.log("Clicked");
               window.dispatchEvent(event);
              }}/>
          <SidebarButton icon={<Bookmark size={14}/>} label={t('sidebar.my_bookmarks')} />
        </nav>
        {appName !== 'RECEIVING' && appName !== 'INVENTORY' && (
          <>
        <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.common_actions')}</div>
        <nav className="p-1">
          <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
        </nav>
          </>
          )}
      </>
    );
  };

  // ==========================================
  // 2.       (DetailView Content)
  // ==========================================
  const renderDetailViewContent = () => {
    if (appName === 'ITEMS')  {
    
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} 
              onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
            />
          </nav>
          <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.workflow_status')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<ShieldAlert size={14} className="text-orange-500" />} label={t('sidebar.change_status')}
            onClick={() => {
              const event = new CustomEvent('GLOBAL_STATUS_HANDLER');
              window.dispatchEvent(event);
            }}
            />
            <SidebarButton icon={<History size={14} className="text-gray-500" />} label={t('sidebar.view_history')} 
             onClick={() => {
              const event = new CustomEvent('OPEN_STATUS_HISTORY');
              window.dispatchEvent(event);
            }}
            />
          </nav>          
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.item_ops')}</div>
          <nav className="p-1">
            <SidebarButton visible={canUpdate} icon={<Box size={14}/>} label={t('sidebar.add_to_storeroom')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_ADD_ITEM_STOREROOM');
              window.dispatchEvent(event);
            }}            
            />
          </nav>
        </>
      );    
    }
    if (appName === 'INVENTORY')  {
    
      return (
        <>
          <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.workflow_status')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<ShieldAlert size={14} className="text-orange-500" />} label={t('sidebar.change_status')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_STATUS_HANDLER');
              window.dispatchEvent(event);
            }}
            />
            <SidebarButton icon={<History size={14} className="text-gray-500" />} label={t('sidebar.view_history')} 
             onClick={() => {
              const event = new CustomEvent('OPEN_STATUS_HISTORY');
              window.dispatchEvent(event);
            }}
            />
          </nav>          
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.inv_ops')}</div>
          <nav className="p-1">
            <SidebarButton icon={<Box size={14}/>} label={t('sidebar.reorder')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_REORDER_HANDLER');
              window.dispatchEvent(event);
            }}            
            />          
          </nav>
        </>
      );    
    }       
    if (appName === 'PR')  {
    
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} 
              onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
            />
          </nav>
          <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.workflow_status')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<ShieldAlert size={14} className="text-orange-500" />} label={t('sidebar.change_status')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_STATUS_HANDLER');
              window.dispatchEvent(event);
            }}
            />
            <SidebarButton icon={<History size={14} className="text-gray-500" />} label={t('sidebar.view_history')} 
             onClick={() => {
              const event = new CustomEvent('OPEN_STATUS_HISTORY');
              window.dispatchEvent(event);
            }}
            />
          </nav>          
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.tax_ops')}</div>
          <nav className="p-1">
            <SidebarButton icon={<Box size={14}/>} label={t('sidebar.tax_category')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_TAX_CATEGORY');
              window.dispatchEvent(event);
            }}            
            />
            {currentPRForPO && (
              <SidebarButton icon={<Plus size={14}/>} label={t('sidebar.create_po')}
                  onClick={() => { 
                    const event = new CustomEvent('OPEN_CREATE_PO'); 
                    window.dispatchEvent(event);
                  }}
                />
            )}            
          </nav>
        </>
      );    
    }    
    if (appName === 'ORGANIZATION')  {
    
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} 
              onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
            />
          </nav>        
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.settings_group')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Box size={14}/>} label={t('sidebar.system_settings')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_ORG_SETTINGS');
              window.dispatchEvent(event);
            }}            
            />
          </nav>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Box size={14}/>} label={t('sidebar.email_settings')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_EMAIL_SETTINGS');
              window.dispatchEvent(event);
            }}            
            />
          </nav>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Box size={14}/>} label={t('ai_settings.title')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_AI_SETTINGS');
              window.dispatchEvent(event);
            }}            
            />
          </nav>
        </>
      );    
    }    
    if (appName === 'ASSET') {
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} 
              onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
            />
          </nav>
          <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.workflow_status')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<ShieldAlert size={14} className="text-orange-500" />} label={t('sidebar.change_status')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_STATUS_HANDLER');
              window.dispatchEvent(event);
            }}
            />
            <SidebarButton icon={<History size={14} className="text-gray-500" />} label={t('sidebar.view_history')} 
             onClick={() => {
              const event = new CustomEvent('OPEN_STATUS_HISTORY');
              window.dispatchEvent(event);
            }}
            />
          </nav>          
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.asset_ops')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Activity size={14}/>} label={t('sidebar.meter_readings')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_METER_READING_HANDLER');
              window.dispatchEvent(event);
            }}            
            />
            <SidebarButton icon={<History size={14}/>} label={t('sidebar.hist_readings')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_METER_HISTORY');
              window.dispatchEvent(event);
            }}            
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Clock size={14}/>} label={t('sidebar.report_downtime')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_DOWNTIME_MODAL');
              window.dispatchEvent(event);
            }}              
            />
            <SidebarButton 
              icon={<History size={14} className="text-red-700" />} 
              label={t('sidebar.downtime_hist')} 
              onClick={() => window.dispatchEvent(new CustomEvent('OPEN_DOWNTIME_HISTORY'))} 
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Move size={14}/>} label={t('sidebar.move_mod_asset')} onClick={() => window.dispatchEvent(new CustomEvent('OPEN_MOVE_MODAL'))} />
            <SidebarButton icon={<Move size={14}/>} label={t('sidebar.asset_move_hist')} onClick={() => window.dispatchEvent(new CustomEvent('OPEN_MOVE_HISTORY'))} />
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.create_related')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<AlertCircle size={14}/>} label={t('sidebar.create_incident')} 
              onClick={() => {
                const event = new CustomEvent('OPEN_INCIDENT_MODAL');
                window.dispatchEvent(event);
              }}
            />
            <SidebarButton icon={<History size={14} className="text-blue-600" />} label={t('sidebar.view_inc_hist')} 
              onClick={() => {
                const event = new CustomEvent('OPEN_INCIDENT_HISTORY');
                window.dispatchEvent(event);
              }}
            />
            <SidebarButton icon={<FileText size={14}/>} label={t('sidebar.create_wo')} />
            <SidebarButton icon={<Share2 size={14}/>} label={t('sidebar.sr')} />
          </nav>
        </>
      );
    }
    if (appName === 'LOCATION') {
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} 
              onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
            />
          </nav>
          <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.workflow_status')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<ShieldAlert size={14} className="text-orange-500" />} label={t('sidebar.change_status')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_STATUS_HANDLER');
              window.dispatchEvent(event);
            }}
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<History size={14} className="text-gray-500" />} label={t('sidebar.view_history')} 
             onClick={() => {
              const event = new CustomEvent('OPEN_STATUS_HISTORY');
              window.dispatchEvent(event);
            }}
            />
          </nav>          
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.asset_ops')}</div>
          <nav className="p-1">
            <SidebarButton icon={<Activity size={14}/>} label={t('sidebar.meter_readings')} 
            onClick={() => {
              const event = new CustomEvent('GLOBAL_METER_READING_HANDLER');
              window.dispatchEvent(event);
            }}            
            />
            <SidebarButton icon={<History size={14}/>} label={t('sidebar.hist_readings')} 
            onClick={() => {
              const event = new CustomEvent('OPEN_METER_HISTORY');
              window.dispatchEvent(event);
            }}            
            />
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.create_related')}</div>
          <nav className="p-1">
            <SidebarButton icon={<AlertCircle size={14}/>} label={t('sidebar.create_incident')} 
              onClick={() => {
                const event = new CustomEvent('OPEN_INCIDENT_MODAL');
                window.dispatchEvent(event);
              }}
            />
            <SidebarButton icon={<History size={14} className="text-blue-600" />} label={t('sidebar.view_inc_hist')} 
              onClick={() => {
                const event = new CustomEvent('OPEN_INCIDENT_HISTORY');
                window.dispatchEvent(event);
              }}
            />
            <SidebarButton icon={<FileText size={14}/>} label={t('sidebar.create_wo')} />
            <SidebarButton icon={<Share2 size={14}/>} label={t('sidebar.sr')} />
          </nav>
        </>
      );
    }
    if (appName === 'REPDESIGNER' || appName === 'RECEIVING' ) {
      return (
        <>
        </>
      );
    }

    if (appName === 'ASSET_CATEGORIES') {
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} 
              onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
            />
          </nav>
        </>
      );
    }

    if (appName === 'WORKORDER') {
      return (
        <>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
          <nav className="p-1">
            <SidebarButton visible={(isNewRecord && canCreate) || (!isNewRecord && canUpdate)} onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
            <SidebarButton visible={canCreate} onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
            <SidebarButton visible={!isNewRecord && canDelete} icon={<Plus size={14}/>} label={`${t('sidebar.delete')} ${translatedApp}`} onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }} 
            />
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.workflow_status')}</div>
          <nav className="p-1">
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<CheckCircle size={14} className="text-green-600"/>} label={t('sidebar.approve_wo')}  
            onClick={() => {
               const event = new CustomEvent('GLOBAL_APPR_WO');
               window.dispatchEvent(event);
              }}
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Play size={14} className="text-blue-600"/>} label={t('sidebar.start_work')} 
              onClick={() => {
                 const event = new CustomEvent('GLOBAL_STRT_WO');
                 window.dispatchEvent(event);
                }}
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<CheckCircle2 size={14} className="text-orange-600"/>} label={t('sidebar.complete_work')} 
              onClick={() => {
                 const event = new CustomEvent('GLOBAL_COMP_WO');
                 window.dispatchEvent(event);
                }}
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<XCircle size={14} className="text-gray-600"/>} label={t('sidebar.close_wo')} 
             onClick={() => {
                 const event = new CustomEvent('GLOBAL_CLOSE_WO');
                 window.dispatchEvent(event);
                }}
            />
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.create_related')}</div>
          <nav className="p-1">
            <SidebarButton icon={<FileCheck size={14}/>} label={t('sidebar.apply_sla')} onClick={() => {
              const event = new CustomEvent('GLOBAL_SLA_HANDLER');
              window.dispatchEvent(event);
            }}/>
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<AlertCircle size={14}/>} label={t('sidebar.create_incident')} />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<Share2 size={14}/>} label={t('sidebar.sr')} />
          </nav>
          <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300 mt-2">{t('sidebar.analytics')}</div>
          <nav className="p-1">
            <SidebarButton icon={<BarChart size={14}/>} label={t('sidebar.wf_history')} onClick={() => {
              const event = new CustomEvent('OPEN_WF_HISTORY');
              window.dispatchEvent(event);
            }} 
            />
            <SidebarButton visible={!isNewRecord && canUpdate} icon={<DollarSign size={14} className="text-green-700"/>} label={t('sidebar.wo_costs')} />
            <SidebarButton icon={<ShoppingCart size={14}/>} label={t('sidebar.purchase_info')} />
          </nav>
        </>
      );
    }

    // Details default view
    return (
      <>
        <div className="p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.record_actions')}</div>
        <nav className="p-1">
          <SidebarButton onClick={triggerSave} icon={<Save size={14} className="text-blue-600"/>} label={`${t('sidebar.save')} ${translatedApp}`} className="bg-white border border-gray-200 mb-1 shadow-sm" />
          <SidebarButton onClick={handleNewRecord} icon={<Plus size={14}/>} label={`${t('sidebar.new')} ${translatedApp}`} />
          <SidebarButton icon={<Trash2 size={14} className="text-red-600"/>} label={`${t('sidebar.delete')} ${translatedApp}`} onClick={() => {
               const event = new CustomEvent('GLOBAL_DELETE_HANDLER');
               window.dispatchEvent(event);
              }}
          />
        </nav>
        <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">{t('sidebar.workflow_status')}</div>
        <nav className="p-1">
          <SidebarButton visible={!isNewRecord && canUpdate} icon={<ShieldAlert size={14} className="text-orange-500" />} label={t('sidebar.change_status')} 
          onClick={() => {
            console.log("Change Status Clicked");
            const event = new CustomEvent('GLOBAL_STATUS_HANDLER');
            window.dispatchEvent(event);
          }}
          />
          <SidebarButton icon={<History size={14} className="text-gray-500" />} label={t('sidebar.view_history')} 
           onClick={() => {
            const event = new CustomEvent('OPEN_STATUS_HISTORY');
            window.dispatchEvent(event);
          }}
          />
        </nav>
      </>
    );
  };
  console.log(appName);
if (appName === 'REPDESIGNER' || appName === 'COBIE' || appName === 'WORKFLOW' || appName === 'UIMPORTER' || appName === 'REPORTWIZARD' || appName === 'FILED_CONFIG') {return null;}
  return (
    <aside className="w-48 bg-[#f8f9fa] border-r border-gray-300 flex flex-col h-full shrink-0 font-sans shadow-inner" style={{ fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "inherit" }}>
      <div className="p-3 border-b border-gray-300 bg-white">
        <div className="relative">
          <input 
            type="text" 
            placeholder={t('sidebar.find_nav')} 
            className={`w-full ps-2 pe-8 py-1.5 text-[11px] border border-gray-300 rounded-sm focus:border-blue-500 outline-none transition-all ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}
          />
          <Search className={`absolute inset-y-0 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'start-2' : 'end-2'} my-auto text-gray-400`} size={12} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {viewType === 'ListView' ? renderListViewContent() : renderDetailViewContent()}
        
        <div className="mt-4 p-2 bg-[#dee2e6]/50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-300">
          {t('sidebar.more_actions')}
        </div>
        <nav className="p-1">
          <SidebarButton icon={<FileSpreadsheet size={14}/>} label={t('sidebar.export_data')} onClick={() => {
            const event = new CustomEvent('GLOBAL_EXPORT');
            window.dispatchEvent(event);
          }}/>
          <SidebarButton icon={<List size={14}/>} label={t('sidebar.audit_log')} onClick={() => window.dispatchEvent(new CustomEvent('GLOBAL_LOG_HANDLER'))} />
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;